import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Key,
  Mail,
  Eye,
  UserCheck,
  UserX,
  ChevronLeft,
  ChevronRight,
  Send,
  FileText
} from 'lucide-react';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { superAdminAPI } from '../../services/api';
import AnalystModal from './AnalystModal';

const AnalystManagement = () => {
  const [analysts, setAnalysts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedAnalyst, setSelectedAnalyst] = useState(null);
  const [selectedAnalysts, setSelectedAnalysts] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // Refs for table scrolling
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);

  // Load analysts on component mount and when pagination changes
  useEffect(() => {
    fetchAnalysts();
  }, [currentPage, pageSize]);

  // Reload analysts when filters change (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(0); // Reset to first page when filtering
      fetchAnalysts();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedStatus]);

  const fetchAnalysts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        size: pageSize,
        sortBy: 'createdAt',
        sortDir: 'desc'
      };

      // Add filters
      if (searchTerm) params.search = searchTerm;
      if (selectedStatus) params.status = selectedStatus;
      params.roles = 'ROLE_CONTENT_SUPERVISOR,ROLE_CONTENT_GENERATOR,ROLE_CONTENT_REVIEWER'; // Fetch all content roles
      params.excludeRoles = 'ROLE_SUPER_ADMIN,ROLE_ADMIN,ROLE_ANALYST'; // Exclude super admins, regular admins, and legacy analysts

      const response = await superAdminAPI.searchUsers(params);
      const data = response.data;

      setAnalysts(data.content || []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error('Error fetching analysts:', error);
      setAnalysts([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnalyst = () => {
    setSelectedAnalyst(null);
    setModalMode('add');
    setShowModal(true);
  };

  const handleEditAnalyst = (analyst) => {
    setSelectedAnalyst(analyst);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewAnalyst = (analyst) => {
    setSelectedAnalyst(analyst);
    setModalMode('view');
    setShowModal(true);
  };

  const handleResetPassword = async (analyst) => {
    if (window.confirm(`Reset password for analyst "${analyst.name}"? A new temporary password will be generated and sent to their email.`)) {
      try {
        const response = await superAdminAPI.resetAnalystPassword(analyst.id);
        alert(`Password reset successfully. New temporary password sent to ${response.data.email}.`);
      } catch (error) {
        console.error('Error resetting analyst password:', error);
        alert('Failed to reset password. Please try again.');
      }
    }
  };

  const handleToggleStatus = async (analyst) => {
    const newStatus = !analyst.enabled;
    const action = newStatus ? 'activate' : 'deactivate';

    if (window.confirm(`Are you sure you want to ${action} ${analyst.name}?`)) {
      try {
        await superAdminAPI.updateUserStatus(analyst.id, { active: newStatus });
        fetchAnalysts(); // Refresh the list
      } catch (error) {
        console.error('Error updating analyst status:', error);
        alert(`Failed to ${action} analyst. Please try again.`);
      }
    }
  };

  const handleDeleteAnalyst = async (analyst) => {
    if (window.confirm(`Are you sure you want to delete ${analyst.name}? This action cannot be undone.`)) {
      try {
        await superAdminAPI.deleteAnalyst(analyst.id);
        fetchAnalysts(); // Refresh the list
      } catch (error) {
        console.error('Error deleting analyst:', error);
        alert('Failed to delete analyst. Please try again.');
      }
    }
  };

  const handleResendWelcomeEmail = async (analyst) => {
    if (!window.confirm(`Are you sure you want to resend the welcome email to ${analyst.name}?\n\nThis will generate a new temporary password and send it to ${analyst.email}.`)) {
      return;
    }

    try {
      await superAdminAPI.resendAnalystWelcomeEmail(analyst.id, {
        platformName: 'BayesPrep Platform'
      });

      alert(`Welcome email resent successfully to ${analyst.email}!\n\nA new temporary password has been generated and sent.`);
      fetchAnalysts(); // Refresh the analyst list to update any status changes
    } catch (error) {
      console.error('Error resending welcome email:', error);
      const errorMessage = error.response?.data?.message || 'Failed to resend welcome email. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleModalSave = (shouldCloseModal = false) => {
    fetchAnalysts(); // Refresh the list
    if (shouldCloseModal) {
      setShowModal(false);
    }
  };

  // Bulk selection handlers
  const handleSelectAnalyst = (analystId) => {
    const newSelected = new Set(selectedAnalysts);
    if (newSelected.has(analystId)) {
      newSelected.delete(analystId);
    } else {
      newSelected.add(analystId);
    }
    setSelectedAnalysts(newSelected);
    setIsSelectAllChecked(newSelected.size === analysts.length && analysts.length > 0);
  };

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedAnalysts(new Set());
      setIsSelectAllChecked(false);
    } else {
      const allAnalystIds = new Set(analysts.map(analyst => analyst.id));
      setSelectedAnalysts(allAnalystIds);
      setIsSelectAllChecked(true);
    }
  };

  const handleBulkDelete = () => {
    if (selectedAnalysts.size === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedAnalysts.size} selected analyst(s)? This action cannot be undone.`)) {
      confirmBulkDelete();
    }
  };

  const confirmBulkDelete = async () => {
    try {
      const analystIds = Array.from(selectedAnalysts);

      // Delete each analyst individually
      for (const analystId of analystIds) {
        await superAdminAPI.deleteAnalyst(analystId);
      }

      setSelectedAnalysts(new Set());
      setIsSelectAllChecked(false);
      fetchAnalysts(); // Refresh the list
      alert(`Successfully deleted ${analystIds.length} analyst(s)!`);
    } catch (error) {
      console.error('Error deleting analysts:', error);
      alert('Failed to delete some analysts. Please try again.');
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page
  };

  const filteredAnalysts = analysts.filter(analyst =>
    analyst.roles && (
      analyst.roles.includes('ROLE_CONTENT_SUPERVISOR') ||
      analyst.roles.includes('ROLE_CONTENT_GENERATOR') ||
      analyst.roles.includes('ROLE_CONTENT_REVIEWER')
    )
  );

  return (
    <div>
      <div className="page-header-compact">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold azure-text mb-1">Content Team Management</h1>
            <p className="text-sm text-blue-700 font-medium">
              Manage content creators, reviewers, supervisors, and legacy analysts
            </p>
          </div>
          <div className="flex space-x-3">
            {selectedAnalysts.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="btn-danger flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedAnalysts.size})
              </button>
            )}
            <button
              onClick={handleAddAnalyst}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Content User
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card mb-4 py-3 px-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search analysts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <select
            className="input-field w-auto"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedStatus('');
              setCurrentPage(0);
            }}
            className="btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Analysts Table */}
      <div className="card">
        {loading ? (
          <CardLoadingSpinner message="Loading analysts..." />
        ) : (
          <div>
            {/* Table Container */}
            <div
              ref={tableScrollRef}
              className="table-container-fixed"
            >
              <div ref={tableRef}>
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="table-header w-10 sticky left-0 bg-slate-50 shadow-lg z-10">
                        <input
                          type="checkbox"
                          checked={isSelectAllChecked}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="table-header w-32 sticky left-10 bg-slate-50 shadow-lg z-10">Actions</th>
                      <th className="table-header">Name</th>
                      <th className="table-header">Email</th>
                      <th className="table-header hidden lg:table-cell">Status</th>
                      <th className="table-header hidden lg:table-cell">First Login</th>
                      <th className="table-header hidden xl:table-cell">Last Login</th>
                      <th className="table-header hidden xl:table-cell">Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {filteredAnalysts.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="table-cell text-center py-12">
                          <div className="text-slate-500">
                            {analysts.length === 0 ? 'No analysts found. Add some analysts to get started.' : 'No analysts match your search criteria.'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredAnalysts.map((analyst) => (
                        <tr key={analyst.id} className="hover:bg-slate-50">
                          <td className="table-cell sticky left-0 shadow-lg z-10 bg-white">
                            <input
                              type="checkbox"
                              checked={selectedAnalysts.has(analyst.id)}
                              onChange={() => handleSelectAnalyst(analyst.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="table-cell sticky left-10 shadow-lg z-10 bg-white">
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleViewAnalyst(analyst)}
                                className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                title="View Analyst"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleEditAnalyst(analyst)}
                                className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 transition-colors duration-200"
                                title="Edit Analyst"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleResetPassword(analyst)}
                                className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 transition-colors duration-200"
                                title="Reset Password"
                              >
                                <Key className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleResendWelcomeEmail(analyst)}
                                className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 transition-colors duration-200"
                                title="Resend Welcome Email"
                              >
                                <Send className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(analyst)}
                                className={`p-1 transition-colors duration-200 ${
                                  analyst.enabled
                                    ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                                    : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                }`}
                                title={analyst.enabled ? "Deactivate Analyst" : "Activate Analyst"}
                              >
                                {analyst.enabled ? <UserX className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />}
                              </button>
                              <button
                                onClick={() => handleDeleteAnalyst(analyst)}
                                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 transition-colors duration-200"
                                title="Delete Analyst"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                          <td className="table-cell font-medium">
                            <div className="flex items-center space-x-2">
                              <span>{analyst.name}</span>
                              {(() => {
                                const getRoleBadge = (roles) => {
                                  if (roles.includes('ROLE_CONTENT_SUPERVISOR')) {
                                    return { text: 'Content Supervisor', color: 'bg-blue-100 text-blue-800' };
                                  } else if (roles.includes('ROLE_CONTENT_GENERATOR')) {
                                    return { text: 'Content Generator', color: 'bg-green-100 text-green-800' };
                                  } else if (roles.includes('ROLE_CONTENT_REVIEWER')) {
                                    return { text: 'Content Reviewer', color: 'bg-orange-100 text-orange-800' };
                                  }
                                  return { text: 'Unknown', color: 'bg-gray-100 text-gray-800' };
                                };
                                const badge = getRoleBadge(analyst.roles || []);
                                return (
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                                    <FileText className="h-3 w-3 mr-1" />
                                    {badge.text}
                                  </span>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="table-cell">{analyst.email}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden lg:table-cell">
                            <span className={`status-badge ${analyst.enabled ? 'status-active' : 'status-inactive'}`}>
                              {analyst.enabled ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden lg:table-cell">
                            <span className={`status-badge ${analyst.firstTimeLogin ? 'status-pending' : 'status-completed'}`}>
                              {analyst.firstTimeLogin ? 'Pending' : 'Completed'}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden xl:table-cell">
                            {analyst.lastLoginAt ? new Date(analyst.lastLoginAt).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 border-b border-slate-100 hidden xl:table-cell">
                            {analyst.createdAt ? new Date(analyst.createdAt).toLocaleDateString() : 'Unknown'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="px-4 sm:px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <span className="text-sm text-slate-700 text-center sm:text-left">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} analysts
                </span>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <label className="text-sm text-slate-700">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="border border-slate-300 rounded px-2 py-1 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-slate-700">per page</span>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center sm:justify-end space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(0, Math.min(totalPages - 5, currentPage - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg border ${
                          pageNum === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Analyst Modal */}
      <AnalystModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        mode={modalMode}
        analyst={selectedAnalyst}
        onSave={handleModalSave}
      />
    </div>
  );
};

export default AnalystManagement;
